import { contentLengthSeconds, formatMinutes } from './content_length';
import { joinMeta, normalizeText } from './ec_text';
import {
  holdsCredit, isUnclaimedCredit, parseActivityTime, timeWatchedFor,
  type CreditRecord, type TimeWatchedEntry, type TimeWatchedMap,
} from './learning_metrics';

/*
-------------------------------
ENRICHMENT CENTER LESSONS
The rules every EC screen applies to a single lesson: which content counts as a
lesson, where its watch entry leaves it (resumable, started, finished), what its
card says (status, CE state, meta line) and where its links go.

isHandout and isFinishedEntry repeat module-private helpers in learning_metrics
and content_order, which stay as they are; these exports are the one public
rule new code uses.
-------------------------------
*/

type Typed = { content_type?: string | null };

const typeOf = (item: Typed) => item.content_type?.toLowerCase();

export const isHandout = (item: Typed): boolean => typeOf(item) === 'handout';

/** Everything that is not a handout, an empty content_type included. */
export const visibleLessons = <C extends Typed>(content: readonly C[]): C[] =>
  content.filter((item) => !isHandout(item));

export const isVideoOrAudio = (item: Typed): boolean => {
  const type = typeOf(item);
  return type === 'video' || type === 'audio';
};

export const isAudio = (item: Typed): boolean => typeOf(item) === 'audio';

/** Whether the item carries a tag with exactly one of these names. */
export const hasTag = (item: { ec_content_tags?: ReadonlyArray<{ name: string }> }, names: readonly string[]): boolean =>
  item.ec_content_tags?.some((tag) => names.includes(tag.name)) ?? false;

/* ---------- watch entries ---------- */

/** Entries arrive untyped from the slice; anything that isn't an object is no entry at all. */
const asEntry = (entry: unknown): TimeWatchedEntry | undefined =>
  typeof entry === 'object' && entry !== null ? (entry as TimeWatchedEntry) : undefined;

/** Seconds come as numbers from the API and may be strings in a saved entry. */
const secondsWatched = (entry: TimeWatchedEntry | undefined): number => Number(entry?.seconds_watched);

export const isFinishedEntry = (entry: unknown): boolean => asEntry(entry)?.video_finished === true;

/** Played partway (seconds > 0) or finished: what "has a watch entry" means. */
export const hasWatchEntry = (entry: unknown): boolean =>
  secondsWatched(asEntry(entry)) > 0 || isFinishedEntry(entry);

/** Started and not finished, the state "N min left" and the progress bar describe. */
const isUnfinishedStart = (entry: TimeWatchedEntry | undefined): boolean =>
  secondsWatched(entry) > 0 && !isFinishedEntry(entry);

/** Started and not finished: Browse's "In progress" filter, the lessons whose cards show "N min left". */
export const isInProgressEntry = (entry: unknown): boolean => isUnfinishedStart(asEntry(entry));

/**
 * The Continue rule: left partway (not finished, seconds > 0), or left partway
 * through a rewatch (finished, seconds short of the furthest point). Seconds
 * that aren't a number give no position to resume, as in resumePosition.
 */
export const isContinueEntry = (entry: unknown): boolean => {
  const tw = asEntry(entry);
  const seconds = secondsWatched(tw);
  if (!tw || !Number.isFinite(seconds)) return false;
  return isFinishedEntry(tw) ? seconds !== Number(tw.max_seconds_watched) : seconds > 0;
};

/**
 * The lesson to resume: of `lessons`, the one whose entry meets the Continue
 * rule and was played most recently (parseActivityTime(created_at); an
 * unreadable time comes last, a tie keeps `lessons` order). Null when none.
 */
export const resumeLesson = <C extends { record_id_: string | number }>(
  lessons: readonly C[],
  timeWatched: TimeWatchedMap,
): C | null => {
  let latest: C | null = null;
  let latestTime = Number.NEGATIVE_INFINITY;
  for (const lesson of lessons) {
    const entry = timeWatchedFor(timeWatched, lesson.record_id_);
    if (!isContinueEntry(entry)) continue;
    const parsed = parseActivityTime(typeof entry?.created_at === 'string' ? entry.created_at : null);
    const time = Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
    if (latest === null || time > latestTime) {
      latest = lesson;
      latestTime = time;
    }
  }
  return latest;
};

/**
 * Whether My Home shows the resume card with #tour-continue-watching: time
 * watched loaded without error and a visible lesson to resume. The guided tour
 * asks the same question, so its Continue step and the card stay in step.
 */
export const showsContinueCard = ({ timeWatchedLoaded, timeWatchedError, content, timeWatched }: {
  timeWatchedLoaded: boolean;
  timeWatchedError: string | null | undefined;
  content: ReadonlyArray<Typed & { record_id_: string | number }>;
  timeWatched: TimeWatchedMap;
}): boolean =>
  timeWatchedLoaded && !timeWatchedError && resumeLesson(visibleLessons(content), timeWatched) !== null;

/* ---------- links ---------- */

/** Tab values that mean "no tab". Older card links wrote `returnPage=undefined`, and cards inherit the URL's value. */
const MISSING_TABS: ReadonlySet<string> = new Set(['undefined', 'null']);

/** The video page for a lesson; `returnPage` is the tab Back returns to ('home' when empty, 'undefined' or 'null'). */
export const lessonHref = (recordId: string | number, returnPage: string, videoCollection?: string | null): string => {
  const tab = !returnPage || MISSING_TABS.has(returnPage) ? 'home' : returnPage;
  const collection = videoCollection ? `&video_collection=${encodeURIComponent(videoCollection)}` : '';
  return `/enrichment_center/video/${encodeURIComponent(recordId)}?returnPage=${encodeURIComponent(tab)}${collection}`;
};

/* ---------- card status ---------- */

/** Length the progress bar measures against when the lesson's own can't be read. */
const FALLBACK_LENGTH_SECONDS = 600;

/** How far through a started, unfinished lesson its last position is, 0 to 1. Null otherwise. */
export const watchedFraction = (item: { length?: unknown }, entry: TimeWatchedEntry | undefined): number | null => {
  if (!isUnfinishedStart(entry)) return null;
  const length = contentLengthSeconds(item.length) ?? FALLBACK_LENGTH_SECONDS;
  return Math.min(1, Math.max(0, secondsWatched(entry) / length));
};

/** "N min left" for a started, unfinished lesson; null when not started, finished or the length can't be read. */
export const minutesLeftLabel = (item: { length?: unknown }, entry: TimeWatchedEntry | undefined): string | null => {
  const length = contentLengthSeconds(item.length);
  if (length === null || !isUnfinishedStart(entry)) return null;
  return `${formatMinutes(Math.max(0, length - secondsWatched(entry)))} left`;
};

export type LessonStatus = { kind: 'minutesLeft'; label: string } | { kind: 'watched' } | { kind: 'upNext' } | null;

/** A card's status line: Watched, N min left, or Up next (only when asked for and not started). */
export const lessonStatus = (
  item: { length?: unknown },
  entry: TimeWatchedEntry | undefined,
  options: { upNext?: boolean } = {},
): LessonStatus => {
  if (isFinishedEntry(entry)) return { kind: 'watched' };
  const label = minutesLeftLabel(item, entry);
  if (label !== null) return { kind: 'minutesLeft', label };
  if (options.upNext && !hasWatchEntry(entry)) return { kind: 'upNext' };
  return null;
};

export type CeState = 'none' | 'earns' | 'claim' | 'claimed';

/**
 * A card's CE state: none without CE; claimed when the viewer holds the credit;
 * claim when finished and unclaimed, judged only once the credits have loaded;
 * otherwise earns.
 */
export const cardCeState = (
  item: { record_id_: string | number; cme_enabled?: unknown },
  entry: TimeWatchedEntry | undefined,
  credits: readonly CreditRecord[],
  creditsStatus: 'idle' | 'loading' | 'succeeded' | 'failed',
): CeState => {
  if (!item.cme_enabled) return 'none';
  if (holdsCredit(item.record_id_, entry, credits)) return 'claimed';
  const candidate = { record_id_: item.record_id_, cme_enabled: item.cme_enabled, time_watched: entry };
  if (creditsStatus === 'succeeded' && isUnclaimedCredit(candidate, credits)) return 'claim';
  return 'earns';
};

/* ---------- card meta ---------- */

export type CardMetaVariant = 'default' | 'speaker' | 'speakerChapter' | 'speakerSeries' | 'seriesChapter';

/**
 * A card's meta line. Screens that say what theirs shows pick a variant; the
 * default is speaker · chapter, else speaker · series, else the speaker.
 */
export const cardMetaLine = (
  variant: CardMetaVariant,
  parts: { speaker?: string | null; chapter?: string | null; series?: string | null },
): string => {
  const speaker = normalizeText(parts.speaker);
  const chapter = normalizeText(parts.chapter);
  const series = normalizeText(parts.series);
  switch (variant) {
    case 'speaker': return joinMeta([speaker]);
    case 'speakerChapter': return joinMeta([speaker, chapter]);
    case 'speakerSeries': return joinMeta([speaker, series]);
    case 'seriesChapter': return joinMeta([series, chapter]);
    default: return joinMeta([speaker, chapter || series]);
  }
};
